import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { useNotify } from '../../components/notifications';
import { runLoading } from '../../components/loading';
import { fmtLocalDate } from '../../utils/datetime';
import { holdTimeFor, hydrateTestRows, ParameterPopup, saveTestStatus, type TestRow } from './SampleTransferPillPopups';
// The shared per-test workflow-status list — the SAME one Registration's Test
// Assignment binds its Status combo to (one source of truth, owner request 2026-09-29).
import { SAMPLE_STATUS_OPTS } from './sampleRegistration/srData';

/**
 * "Test Assignment" master-detail under the Sample Login grid — the second grid
 * Sample Registration shows (user request 2026-09-23: "design exact like
 * registration"). Marking a Sample Login row above switches the sample here.
 *
 * Add / Delete act HERE, in place (owner request 2026-09-23), through
 * Sample Transfer's OWN narrow endpoints rather than Registration's job-wide
 * save. That save (AI_SampleRegistration_SaveChildren_SP) replace-alls the job's
 * SampleParameter AND SampleBottleAllocation rows, and its bottle re-insert drops
 * the transfer receipt columns — so driving it from here would wipe the receipt
 * record of every bottle that is not signed off. See
 * deploy/db/AI_SampleTransfer_Test_SPs.sql for the full reasoning and the bottle
 * rule (a bottle is one distinct container+preservative pair, lettered A, B, C …;
 * field tests get none).
 *
 * Column set matches Registration exactly. Storage is blank and HoldTime is
 * derived from the method because Registration does the same (srData.ts) — there
 * is no stored source for either.
 */
export default function SampleTransferTestAssignment(
  { jobOid, sampleId }: { jobOid: string; sampleId: string | null; sampleOid: string | null },
) {
  const notify = useNotify();
  const [rows, setRows] = useState<TestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  // The row whose Parameter cell was clicked → opens the analyte checked-list popup
  // (owner request 2026-09-29: a clickable combo, exactly like Registration).
  const [paramRow, setParamRow] = useState<TestRow | null>(null);
  // The row whose BottleID cell was clicked → opens the editable bottle popup
  // (owner request 2026-09-29: parity with Registration's bottle picker).
  const [bottleRow, setBottleRow] = useState<TestRow | null>(null);

  const load = useCallback(() => {
    if (!jobOid) return;
    setLoading(true);
    api.get(`/api/v1/sample-transfer/${jobOid}/tests`)
      .then(r => setRows(hydrateTestRows((r.data as { data: TestRow[] }).data ?? [])))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [jobOid]);
  useEffect(() => { load(); }, [load]);

  // Master-detail: only the marked sample's tests, like Registration.
  const shown = useMemo(
    () => (sampleId ? rows.filter(r => r.sampleId === sampleId) : []),
    [rows, sampleId]);

  // The picker filters the catalog to the sample's matrix, as Registration filters
  // Switching sample must not carry the previous sample's ticks over.
  useEffect(() => { setSel(new Set()); }, [sampleId]);

  /** Status combo commit → Sample Transfer's OWN receipt-safe PUT /test-status, then
   *  reload so the derived SubOut tick refreshes (parity with Registration). */
  const commitStatus = useCallback((row: TestRow, value: string) => {
    void saveTestStatus(row.oid, value)
      .then(() => { notify.success('Saved successfully.'); load(); })
      .catch((e: unknown) => {
        const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
        notify.error(err.response?.data?.error?.message ?? err.message ?? 'Failed to save the status.');
      });
  }, [notify, load]);

  const cols: AppColumn<TestRow>[] = [
    { field: 'test', header: 'Test', width: 150 },
    { field: 'testGroupName', header: 'TestGroupName', width: 130 },
    // Clickable combo → the editable bottle popup, exactly like Registration's
    // BottleID cell (owner request 2026-09-29). Uses Sample Transfer's OWN,
    // receipt-safe bottle save (PUT /test-bottles) — never Registration's job-wide save.
    { field: 'bottleId', header: 'BottleID', width: 90, sortable: false,
      render: t => (
        <div className="stx-cell-click" role="button" tabIndex={0}
          onClick={() => setBottleRow(t)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setBottleRow(t); } }}>
          {t.bottleId || '—'} ▾
        </div>),
      text: t => t.bottleId },
    { field: 'method', header: 'Method', width: 120 },
    { field: 'components', header: 'Component', width: 110 },
    // Clickable combo → the analyte checked-list popup, exactly like Registration's
    // Test Assignment Parameter cell (owner request 2026-09-29). Uses Sample
    // Transfer's OWN ParameterPopup + PUT /test-parameters endpoint.
    { field: 'parameter', header: 'Parameter', width: 120, sortable: false,
      render: t => (
        <div className="stx-cell-click" role="button" tabIndex={0}
          onClick={() => setParamRow(t)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setParamRow(t); } }}>
          {t.parameter} ▾
        </div>),
      text: t => t.parameter },
    // The first bottle's storage name (e.g. "Micro"), from the reader — Registration
    // shows this in the Test Assignment grid (owner report 2026-09-29).
    { field: 'storage', header: 'Storage', width: 100, text: t => t.storage },
    { field: 'hdTime', header: 'HoldTime', width: 110, sortable: false,
      render: t => holdTimeFor(t.test, t.method), text: t => holdTimeFor(t.test, t.method) },
    // Editable Status dropdown — the SAME shared combo Registration's Test Assignment
    // uses (SAMPLE_STATUS_OPTS), committing through the receipt-safe PUT /test-status.
    { field: 'status', header: 'Status', width: 140,
      edit: { type: 'combo', options: SAMPLE_STATUS_OPTS } },
    // Editable checkbox, exactly like Registration's Test Assignment Remark — a local
    // toggle (Registration's per-test remark is in-memory too; its save only writes the
    // SAMPLE Comment, not the per-test one). Owner report 2026-09-29: it was disabled here.
    { field: 'remark', header: 'Remark', width: 70, align: 'center', sortable: false, filterable: false,
      render: t => (
        <input type="checkbox" checked={t.remark} aria-label={`Remark for ${t.test}`}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const checked = e.target.checked;
            setRows(rs => rs.map(r => (r.oid === t.oid ? { ...r, remark: checked } : r)));
          }} />) },
    { field: 'subOut', header: 'SubOut', width: 70, align: 'center', sortable: false, filterable: false,
      render: t => <input type="checkbox" checked={t.subOut} disabled aria-label={`Sub out ${t.test}`} /> },
    { field: 'testAddedDate', header: 'TestAddedDate', width: 130,
      render: t => (t.testAddedDate ? fmtLocalDate(t.testAddedDate) : ''),
      text: t => (t.testAddedDate ? fmtLocalDate(t.testAddedDate) : '') },
  ];

  // Add / Delete are NOT part of Sample Transfer — legacy's Test Assignment views are
  // AllowNew=False / AllowDelete=False (Model.xafml), so tests can't be added or removed
  // here; only the Status / Bottle / Parameter cells are edited (owner report 2026-09-30).

  return (
    <div className="stx-ta">
      <div className="stx-ta-head">
        <b>Test Assignment{sampleId ? ' - ' : ''}<span style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))' }}>{sampleId}</span></b>
      </div>
      <div className="stx-ta-grid">
        <AppDataGrid<TestRow>
          rows={shown} columns={cols} getRowId={r => r.oid}
          selectable selectedIds={sel} onSelectionChange={setSel}
          onCellCommit={(row, col, value) => { if (col.field === 'status') commitStatus(row, value); }}
          maximizable exportFilename={`TestAssignment_${sampleId ?? jobOid}`}
          defaultPageSize={20}
          loading={loading}
          emptyText={sampleId ? 'No tests for this sample' : 'Mark a sample above to see its tests'} />
      </div>

      {paramRow && (
        <ParameterPopup row={paramRow} onClose={() => setParamRow(null)} onSaved={load} />
      )}

      {bottleRow && (
        <BottlePickerPopup row={bottleRow} onClose={() => setBottleRow(null)} onSaved={load} />
      )}
    </div>
  );
}

// ─── Editable BottleID popup ──────────────────────────────────────────────────
/** Option list row from GET /sample-registration/bottle-lookups (shared reference
 *  data — the same lists Registration's picker shows). */
interface LookupOpt { oid: string; name: string; }
interface BottleLookupsDto {
  containers: LookupOpt[]; preservatives: LookupOpt[];
  storages: LookupOpt[]; storageConditions: LookupOpt[];
}
/** One bottle from GET /sample-transfer/test-bottles/{oid} (Oids). */
interface TestBottleDto {
  bottleId: string; containerOid: string | null; preservativeOid: string | null;
  storageOid: string | null; storageConditionOid: string | null; signedOff: boolean;
}
interface TestBottlesDto { qty: number; testMethodOid: string | null; bottles: TestBottleDto[]; }
/** Per-letter editable detail (Oids). */
interface BottleDetail { container: string; preserv: string; storage: string; storageCondition: string; }

/**
 * Editable bottle picker for one test — the SAME layout as Registration's
 * BottlePickerDialog (Bottle ID · Container · Preservation · Storage Condition ·
 * Storage, per bottle letter A…Qty), but wired to Sample Transfer's OWN endpoints:
 * GET/PUT /sample-transfer/test-bottles/{parameterOid}. The save is receipt-safe
 * (reconciles in place; never Registration's job-wide save). Option lists come from
 * the shared bottle-lookups reference endpoint. A signed-off bottle is read-only.
 */
function BottlePickerPopup({ row, onClose, onSaved }:
  { row: TestRow; onClose: () => void; onSaved: () => void }) {
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [qty, setQty] = useState(1);
  const [lk, setLk] = useState<BottleLookupsDto>({ containers: [], preservatives: [], storages: [], storageConditions: [] });
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [locked, setLocked] = useState<Set<string>>(new Set());  // signed-off letters (read-only)
  const [bd, setBd] = useState<Record<string, BottleDetail>>({});

  useEffect(() => {
    let live = true;
    setLoading(true);
    api.get(`/api/v1/sample-transfer/test-bottles/${row.oid}`)
      .then(async r => {
        if (!live) return;
        const d = (r.data as { data: TestBottlesDto }).data;
        const q = Math.max(1, d.qty || 1);
        setQty(q);
        // Pre-tick the letters that already have an allocation; seed their detail.
        const p = new Set<string>();
        const sg = new Set<string>();
        const seed: Record<string, BottleDetail> = {};
        for (const b of d.bottles ?? []) {
          p.add(b.bottleId);
          if (b.signedOff) sg.add(b.bottleId);
          seed[b.bottleId] = {
            container: b.containerOid ?? '', preserv: b.preservativeOid ?? '',
            storage: b.storageOid ?? '', storageCondition: b.storageConditionOid ?? '',
          };
        }
        if (p.size === 0) p.add('A');
        for (const L of lettersOf(q)) seed[L] ??= { container: '', preserv: '', storage: '', storageCondition: '' };
        setPicked(p); setLocked(sg); setBd(seed);
        // Load the option lists, filtered to this test's method (shared reference data).
        const opt = d.testMethodOid ? { testMethodOids: d.testMethodOid } : undefined;
        const lr = await api.get('/api/v1/sample-registration/bottle-lookups', { params: opt });
        if (live) setLk((lr.data as { data: BottleLookupsDto }).data);
      })
      .catch(() => { if (live) notify.error('Failed to load the bottle setup.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [row.oid, notify]);

  const set = (L: string, k: keyof BottleDetail, v: string) =>
    setBd(m => ({ ...m, [L]: { ...(m[L] ?? { container: '', preserv: '', storage: '', storageCondition: '' }), [k]: v } }));

  const apply = async () => {
    const bottles = [...picked].sort().map(L => {
      const d = bd[L] ?? { container: '', preserv: '', storage: '', storageCondition: '' };
      return {
        bottleId: L,   // the letter only — the SP scopes it to this sample + test
        containerOid: d.container || null, preservativeOid: d.preserv || null,
        storageOid: d.storage || null, storageConditionOid: d.storageCondition || null,
      };
    });
    try {
      await runLoading(
        api.put(`/api/v1/sample-transfer/test-bottles/${row.oid}`, { bottles }),
        { blocking: true, label: 'Saving bottles…' });
      notify.success('Saved successfully.');
      onSaved();
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
      notify.error(err.response?.data?.error?.message ?? err.message ?? 'Failed to save the bottles.');
    }
  };

  const letters = lettersOf(qty);
  return (
    <AppDialog open width={1000} icon="ti-bottle"
      title={`${row.test} · bottles (Qty ${qty})`} onClose={onClose}
      footer={<>
        <button className="ca-btn" onClick={onClose}>Cancel</button>
        <button className="ca-btn ca-btn-solid" onClick={() => void apply()} disabled={loading}>Apply</button>
      </>}>
      <div style={{ maxHeight: 300, overflow: 'auto', border: '1px solid var(--ai-line)', borderRadius: 4 }}>
        <table className="sr-pick-table" style={{ tableLayout: 'fixed', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: '3%' }} />
              <th style={{ width: '16%' }}>Bottle ID</th>
              <th style={{ width: '22%' }}>Container</th>
              <th style={{ width: '17%' }}>Preservation</th>
              <th style={{ width: '22%' }}>Storage Condition</th>
              <th style={{ width: '20%' }}>Storage</th>
            </tr>
          </thead>
          <tbody>
            {letters.map(L => {
              const d = bd[L] ?? { container: '', preserv: '', storage: '', storageCondition: '' };
              const on = picked.has(L);
              const lock = locked.has(L);   // signed-off → read-only
              return (
                <tr key={L} className={on ? 'on' : ''}>
                  <td className="sr-pick-cb">
                    <input type="checkbox" checked={on} disabled={lock} aria-label={`Bottle ${L}`}
                      onChange={() => setPicked(p => { const n = new Set(p); if (n.has(L)) n.delete(L); else n.add(L); return n; })} />
                  </td>
                  <td style={{ fontWeight: 600 }}>{row.sampleId}-{L}{lock ? ' 🔒' : ''}</td>
                  <td>
                    <select value={d.container} disabled={lock} aria-label={`Bottle ${L} container`}
                      onChange={e => set(L, 'container', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {lk.containers.map(c => <option key={c.oid} value={c.oid}>{c.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={d.preserv} disabled={lock} aria-label={`Bottle ${L} preservation`}
                      onChange={e => set(L, 'preserv', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {lk.preservatives.map(p => <option key={p.oid} value={p.oid}>{p.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={d.storageCondition} disabled={lock} aria-label={`Bottle ${L} storage condition`}
                      onChange={e => set(L, 'storageCondition', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {lk.storageConditions.map(sc => <option key={sc.oid} value={sc.oid}>{sc.name}</option>)}
                    </select>
                  </td>
                  <td>
                    <select value={d.storage} disabled={lock} aria-label={`Bottle ${L} storage`}
                      onChange={e => set(L, 'storage', e.target.value)} style={CELL_INPUT}>
                      <option value="">N/A</option>
                      {lk.storages.map(s => <option key={s.oid} value={s.oid}>{s.name}</option>)}
                    </select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sr-pick-g" style={{ border: 'none', background: 'none', paddingLeft: 0 }}>
        <span>Editing a bottle keeps its transfer receipt record. A signed-off bottle 🔒 is read-only.</span>
        <span>{picked.size} of {qty} selected</span>
      </div>
    </AppDialog>
  );
}

/** A…(qty) bottle letters. */
function lettersOf(qty: number): string[] {
  return Array.from({ length: Math.max(1, qty) }, (_, i) => String.fromCharCode(65 + i));
}

const CELL_INPUT: CSSProperties = {
  width: '100%', boxSizing: 'border-box', border: '1px solid var(--ai-line)',
  borderRadius: 3, padding: '3px 6px', fontSize: 12,
};

/** One selectable row of the test catalog. */